import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
   Box,
   Container,
   Typography,
   Paper,
   TextField,
   Button,
   ButtonGroup,
   List,
   ListItem,
   ListItemText,
   ListItemAvatar,
   Avatar,
   Divider,
   IconButton,
   CircularProgress,
   Alert,
   Grid,
   Chip,
   InputAdornment,
   Menu,
   MenuItem,
   Drawer,
   Toolbar,
   AppBar,
   Tab,
   Tabs,
   Card,
   CardMedia,
} from '@mui/material';
import {
   Send as SendIcon,
   AttachFile as AttachFileIcon,
   EmojiEmotions as EmojiIcon,
   MoreVert as MoreVertIcon,
   Search as SearchIcon,
   Add as AddIcon,
   FilterList as FilterListIcon,
   Settings as SettingsIcon,
   Forum as ForumIcon,
   People as MembersIcon,
   Groups as GroupsIcon,
   InsertPhoto as PhotoIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUser } from '../../../contexts/UserContext';
import { OrganizationMember } from '../../../types/Organization';
import {
   collection,
   query,
   where,
   getDocs,
   addDoc,
   serverTimestamp,
   orderBy,
   onSnapshot,
   Timestamp,
   doc,
   getDoc,
   limit,
   setDoc,
   updateDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { formatDistanceToNow } from 'date-fns';
import NewChannelDialog from './NewChannelDialog';
import AssetSelectorDialog from './AssetSelectorDialog';

interface Message {
   id: string;
   content: string;
   sender: {
      id: string;
      name: string;
      avatarUrl?: string;
   };
   timestamp: Date | any;
   attachments?: Array<{
      id: string;
      name: string;
      url: string;
      type: string;
      imageUrl?: string;
      thumbnailUrl?: string;
   }>;
   isRead: boolean;
}

interface Channel {
   id: string;
   name: string;
   displayName?: string;
   description?: string;
   type: 'public' | 'private' | 'direct' | 'subscription' | 'event' | 'trip';
   members: string[] | number;
   memberCount?: number;
   unreadCount: number;
   lastActivity?: Date | any;
   memberDetail?: OrganizationMember;
   createdBy?: string;
}

interface Organization {
   id: string;
   name: string;
   description?: string;
   logo?: string;
   memberCount?: number;
   createdAt?: any;
   updatedAt?: any;
}

interface Asset {
   id: string;
   name: string;
   description?: string;
   imageUrl: string;
   thumbnailUrl?: string;
   type: string;
   createdAt: any;
   organizationId: string;
   eventId?: string;
   tripId?: string;
   tags?: string[];
}

interface Subscription {
   id: string;
   name?: string;
   organizationId: string;
   active: boolean;
   [key: string]: any; // Allow for other properties
}

const OrganizationChat: React.FC = () => {
   const { id } = useParams<{ id: string }>();
   const { activeOrganization, isPersonalAccount, switchToOrganization, members, loadingMembers } =
      useOrganization();
   const { currentUser } = useUser();

   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [message, setMessage] = useState('');
   const [messages, setMessages] = useState<Message[]>([]);
   const [channels, setChannels] = useState<Channel[]>([]);
   const [activeChannel, setActiveChannel] = useState<Channel | null>(null);
   const [currentTab, setCurrentTab] = useState(0);
   const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
   const [newChannelDialogOpen, setNewChannelDialogOpen] = useState(false);
   const [assetSelectorOpen, setAssetSelectorOpen] = useState(false);
   const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
   const [activeTab, setActiveTab] = useState<
      'all' | 'events' | 'general' | 'direct' | 'subscriptions' | 'trips'
   >('all');
   const [filteredChannels, setFilteredChannels] = useState<Channel[]>([]);
   const [subscriptionChannels, setSubscriptionChannels] = useState<Channel[]>([]);
   const [tripChannels, setTripChannels] = useState<Channel[]>([]);
   const [recentlyUsedAssets, setRecentlyUsedAssets] = useState<Asset[]>([]);

   const messagesEndRef = useRef<HTMLDivElement>(null);
   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
   const [sidebarOpen, setSidebarOpen] = useState(true);

   // State for initial tooltip about asset gallery feature
   const [showAssetTip, setShowAssetTip] = useState(true);

   useEffect(() => {
      // Check if we've shown the tip before
      const assetTipShown = localStorage.getItem(`org_${id}_asset_tip_shown`);
      if (assetTipShown) {
         setShowAssetTip(false);
      }
   }, [id]);

   const handleDismissAssetTip = () => {
      setShowAssetTip(false);
      try {
         localStorage.setItem(`org_${id}_asset_tip_shown`, 'true');
      } catch (e) {
         console.warn('Failed to save tip state to localStorage:', e);
      }
   };

   useEffect(() => {
      // If we have an organization ID but we're in personal account mode,
      // switch to the organization
      if (id && isPersonalAccount) {
         switchToOrganization(id);
      }

      // Fetch channels and messages
      if (id) {
         fetchChannels();
         // Get organization members
         fetchOrganizationMembers();
      }
   }, [id, isPersonalAccount, switchToOrganization]);

   useEffect(() => {
      // Update orgMembers when members from the context changes
      if (members && members.length > 0) {
         setOrgMembers(members);
      }
   }, [members]);

   useEffect(() => {
      // Scroll to bottom of messages
      scrollToBottom();
   }, [messages]);

   useEffect(() => {
      // Update filtered channels when channels or active tab changes
      if (channels.length === 0) {
         setFilteredChannels([]);
         return;
      }

      switch (activeTab) {
         case 'events':
            // Filter for event-related channels - look for type 'event' or event in name/description
            setFilteredChannels(
               channels.filter(
                  (channel) =>
                     channel.type === 'event' ||
                     channel.name.toLowerCase().includes('event') ||
                     channel.description?.toLowerCase().includes('event')
               )
            );
            break;
         case 'general':
            // Filter for general channels (public channels that aren't event-specific)
            setFilteredChannels(
               channels.filter(
                  (channel) =>
                     channel.type === 'public' && !channel.name.toLowerCase().includes('event')
               )
            );
            break;
         case 'direct':
            // Direct messages between members
            setFilteredChannels(channels.filter((channel) => channel.type === 'direct'));
            break;
         case 'subscriptions':
            setFilteredChannels(subscriptionChannels);
            break;
         case 'trips':
            setFilteredChannels(tripChannels);
            break;
         case 'all':
         default:
            // Show all channels by default (including direct ones)
            setFilteredChannels(channels);
            break;
      }
   }, [channels, activeTab, subscriptionChannels, tripChannels]);

   const fetchOrganizationMembers = async () => {
      if (!id) return;

      try {
         // We use the members from our context instead of fetching again
         if (members && members.length > 0) {
            setOrgMembers(members);
         } else {
            // Fallback if context members aren't available
            const memberQuery = query(
               collection(db, 'organizations', id, 'members'),
               where('status', '==', 'active')
            );

            const memberSnapshot = await getDocs(memberQuery);
            const membersList: OrganizationMember[] = [];

            memberSnapshot.forEach((doc) => {
               membersList.push({ id: doc.id, ...doc.data() } as OrganizationMember);
            });

            setOrgMembers(membersList);
         }
      } catch (error) {
         console.error('Error fetching organization members:', error);
      }
   };

   const fetchChannels = async () => {
      if (!id || !currentUser) return;

      try {
         setLoading(true);

         // First, fetch all organization channels including events
         const orgChannelsQuery = query(
            collection(db, 'channels'),
            where('organizationId', '==', id),
            where('isPersonal', '==', false),
            orderBy('createdAt', 'desc')
         );

         // Set up real-time listener
         return onSnapshot(
            orgChannelsQuery,
            (snapshot) => {
               const channelList: Channel[] = [];

               snapshot.docs.forEach((doc) => {
                  const data = doc.data();
                  channelList.push({
                     id: doc.id,
                     name: data.entityName || data.name || 'Untitled Channel',
                     displayName: data.entityName || data.name || 'Untitled Channel',
                     description: data.description || '',
                     type: data.type || 'public',
                     members: data.members || [],
                     memberCount: data.memberCount || (data.members ? data.members.length : 0),
                     unreadCount: data.unreadCount || 0,
                     lastActivity: data.lastActivity ? data.lastActivity.toDate() : null,
                     createdBy: data.createdBy,
                  });
               });

               setChannels(channelList);
               // Initial filtering
               setFilteredChannels(channelList);
               setLoading(false);
            },
            (err) => {
               console.error('Error fetching organization channels:', err);
               setError('Failed to load channels');
               setLoading(false);
            }
         );
      } catch (err) {
         console.error('Error setting up channels listener:', err);
         setError('Failed to load channels');
         setLoading(false);
         return null;
      }
   };

   const createGeneralChannel = async (): Promise<Channel> => {
      if (!id || !currentUser) {
         throw new Error('Missing organization ID or user');
      }

      try {
         // Check if general channel already exists
         const generalQuery = query(
            collection(db, 'channels'),
            where('organizationId', '==', id),
            where('name', '==', 'general')
         );

         const generalSnapshot = await getDocs(generalQuery);

         if (!generalSnapshot.empty) {
            // Channel already exists, return it
            const data = generalSnapshot.docs[0].data();
            return {
               id: generalSnapshot.docs[0].id,
               name: data.entityName || data.name || 'Untitled Channel',
               displayName: data.entityName || data.name || 'Untitled Channel',
               description: data.description || '',
               type: data.type || 'public',
               members: data.members || [],
               memberCount: data.memberCount || (data.members ? data.members.length : 0),
               unreadCount: data.unreadCount || 0,
               lastActivity: data.lastActivity ? data.lastActivity.toDate() : null,
               createdBy: data.createdBy,
            } as Channel;
         }

         // Create general channel
         const generalData = {
            name: 'general',
            displayName: 'General',
            description: 'General discussions for everyone in the organization',
            type: 'public' as 'public',
            members: [currentUser.uid],
            memberCount: 1,
            organizationId: id,
            createdBy: currentUser.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            lastActivity: serverTimestamp(),
         };

         const generalRef = await addDoc(collection(db, 'channels'), generalData);

         // Add welcome message
         await addDoc(collection(db, 'channels', generalRef.id, 'messages'), {
            content: `Welcome to the ${activeOrganization?.name || 'organization'} chat! This is the general channel where everyone can communicate.`,
            senderId: currentUser.uid,
            senderName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Admin',
            senderAvatar: currentUser.photoURL || '',
            timestamp: serverTimestamp(),
            isRead: false,
         });

         // Log activity
         await addDoc(collection(db, 'organizations', id, 'activityLogs'), {
            userId: currentUser.uid,
            action: 'channel_created',
            details: {
               channelName: 'general',
               channelType: 'public',
            },
            timestamp: serverTimestamp(),
         });

         return {
            id: generalRef.id,
            name: 'general',
            displayName: 'General',
            description: 'General discussions for everyone in the organization',
            type: 'public' as 'public',
            members: [currentUser.uid],
            memberCount: 1,
            unreadCount: 0,
            lastActivity: new Date(),
            createdBy: currentUser.uid,
         } as Channel;
      } catch (err) {
         console.error('Error creating general channel:', err);
         throw err;
      }
   };

   const createDirectMessageChannels = async (): Promise<Channel[]> => {
      if (!currentUser || !orgMembers || orgMembers.length === 0 || !id) return [];

      try {
         // Create direct message channels for each organization member except the current user
         const directChannels: Channel[] = [];

         for (const member of orgMembers.filter((m) => m.userId !== currentUser.uid)) {
            // Create channel ID using sorted user IDs to ensure consistency
            const memberIds = [currentUser.uid, member.userId].sort();
            const channelId = `dm-${memberIds.join('-')}`;

            // Check if direct message channel already exists in Firestore
            const dmRef = doc(db, 'channels', channelId);
            const dmDoc = await getDoc(dmRef);

            let channel: Channel;

            if (dmDoc.exists()) {
               // Channel exists, use the data
               const data = dmDoc.data();
               channel = {
                  id: channelId,
                  name: member.name || member.displayName || member.email,
                  displayName: member.name || member.displayName || member.email,
                  type: 'direct' as 'direct',
                  members: memberIds, // Add the 'members' property
                  unreadCount: 0, // Add the missing required property
               };
            } else {
               // Create new direct message channel
               const dmData = {
                  name: `dm-${memberIds.join('-')}`,
                  displayName: member.name || member.displayName || member.email,
                  type: 'direct' as 'direct',
                  members: memberIds,
                  memberCount: 2,
                  organizationId: id,
                  createdBy: currentUser.uid,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                  lastActivity: serverTimestamp(),
               };

               // Save to Firestore with custom ID
               await setDoc(dmRef, dmData);

               channel = {
                  id: channelId,
                  name: member.name || member.displayName || member.email,
                  displayName: member.name || member.displayName || member.email,
                  type: 'direct' as 'direct',
                  members: memberIds, // Add the 'members' property
                  unreadCount: 0, // Add the missing required property
               };
            }

            directChannels.push(channel);
         }

         return directChannels;
      } catch (err) {
         console.error('Error creating direct message channels:', err);
         return [];
      }
   };

   const fetchMessages = async (channelId: string) => {
      if (!id || !channelId) return;

      try {
         // Set up real-time listener for messages
         const messagesQuery = query(
            collection(db, 'channels', channelId, 'messages'),
            orderBy('timestamp', 'asc'),
            limit(50) // Limit to recent messages
         );

         const unsubscribe = onSnapshot(
            messagesQuery,
            async (snapshot) => {
               const fetchedMessages: Message[] = [];

               // Gather all sender IDs to lookup user info
               const senderIds = new Set<string>();
               snapshot.docs.forEach((doc) => {
                  const data = doc.data();
                  if (data.senderId) {
                     senderIds.add(data.senderId);
                  }
               });

               // Create a map of user IDs to names
               const userMap: Record<string, { name: string; avatarUrl: string }> = {};

               // Add current user to the map
               if (currentUser) {
                  userMap[currentUser.uid] = {
                     name: currentUser.displayName || currentUser.email?.split('@')[0] || 'You',
                     avatarUrl: currentUser.photoURL || '',
                  };
               }

               // Add organization members to the map
               orgMembers.forEach((member) => {
                  if (member.userId) {
                     userMap[member.userId] = {
                        name: member.name || member.displayName || member.email.split('@')[0],
                        avatarUrl: member.avatarUrl || member.photoURL || '',
                     };
                  }
               });

               // For any remaining IDs, try to lookup info from Firestore
               const remainingIds = Array.from(senderIds).filter((id) => !userMap[id]);
               if (remainingIds.length > 0) {
                  try {
                     const userLookups = await Promise.all(
                        remainingIds.map(async (userId) => {
                           try {
                              // First check if it's a test user (which would start with 'test-user-')
                              if (userId.startsWith('test-user-')) {
                                 return {
                                    id: userId,
                                    name: `Test User ${userId.split('-').pop()?.substring(0, 4) || ''}`,
                                    avatarUrl: '',
                                 };
                              }

                              // Try to get user from members collection
                              const memberQuery = query(
                                 collection(db, 'organizations', id, 'members'),
                                 where('userId', '==', userId)
                              );

                              const memberSnap = await getDocs(memberQuery);

                              if (!memberSnap.empty) {
                                 const memberData = memberSnap.docs[0].data();
                                 return {
                                    id: userId,
                                    name:
                                       memberData.name ||
                                       memberData.displayName ||
                                       memberData.email.split('@')[0],
                                    avatarUrl: memberData.avatarUrl || memberData.photoURL || '',
                                 };
                              }

                              // If not found in members, try to get from Firebase auth user records
                              // Since we can't directly access Auth users, we'll return a default format
                              return {
                                 id: userId,
                                 name: userId.substring(0, 6) + '...', // Shortened ID as fallback
                                 avatarUrl: '',
                              };
                           } catch (err) {
                              console.error(`Error looking up user ${userId}:`, err);
                              return { id: userId, name: 'Unknown User', avatarUrl: '' };
                           }
                        })
                     );

                     // Add looked up users to the map
                     userLookups.forEach((user) => {
                        if (user && user.id) {
                           userMap[user.id] = { name: user.name, avatarUrl: user.avatarUrl };
                        }
                     });
                  } catch (err) {
                     console.error('Error looking up users:', err);
                  }
               }

               // Process all messages with user info
               snapshot.forEach((doc) => {
                  const data = doc.data();
                  const senderId = data.senderId || 'unknown';
                  const senderInfo = userMap[senderId] || {
                     name: data.senderName || senderId.substring(0, 6) + '...',
                     avatarUrl: data.senderAvatar || '',
                  };

                  fetchedMessages.push({
                     id: doc.id,
                     content: data.content,
                     sender: {
                        id: senderId,
                        name: senderInfo.name,
                        avatarUrl: senderInfo.avatarUrl,
                     },
                     timestamp: data.timestamp
                        ? new Date(data.timestamp.seconds * 1000)
                        : new Date(),
                     attachments: data.attachments || [],
                     isRead: data.isRead || false,
                  });
               });

               setMessages(fetchedMessages);

               // Mark messages as read
               markMessagesAsRead(channelId);
            },
            (err) => {
               console.error('Error in messages listener:', err);
            }
         );

         return () => unsubscribe();
      } catch (error) {
         console.error('Error fetching messages:', error);
         setError('Failed to load messages. Please try again later.');
      }
   };

   const markMessagesAsRead = async (channelId: string) => {
      if (!id || !channelId || !currentUser) return;

      try {
         // Update unread messages to read
         const unreadQuery = query(
            collection(db, 'channels', channelId, 'messages'),
            where('isRead', '==', false),
            where('senderId', '!=', currentUser.uid)
         );

         const unreadSnapshot = await getDocs(unreadQuery);

         const batch = [];
         for (const doc of unreadSnapshot.docs) {
            batch.push(updateDoc(doc.ref, { isRead: true }));
         }

         if (batch.length > 0) {
            await Promise.all(batch);
         }
      } catch (err) {
         console.error('Error marking messages as read:', err);
      }
   };

   const handleChannelSelect = (channel: Channel) => {
      setActiveChannel(channel);
      fetchMessages(channel.id);
   };

   const handleSendMessage = async () => {
      if ((!message.trim() && selectedAssets.length === 0) || !activeChannel || !id || !currentUser)
         return;

      try {
         // Show visual feedback that we're processing the assets
         if (selectedAssets.length > 0) {
            // You could set a loading state here if needed
         }

         // Prepare attachments from selected assets
         const attachments = selectedAssets.map((asset) => ({
            id: asset.id,
            name: asset.name,
            url: asset.imageUrl,
            type: asset.type || 'image',
            imageUrl: asset.imageUrl,
            thumbnailUrl: asset.thumbnailUrl || asset.imageUrl,
         }));

         // Add message to Firestore
         await addDoc(collection(db, 'channels', activeChannel.id, 'messages'), {
            content: message.trim(),
            senderId: currentUser.uid,
            senderName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
            senderAvatar: currentUser.photoURL || '',
            timestamp: serverTimestamp(),
            isRead: false,
            attachments: attachments.length > 0 ? attachments : undefined,
         });

         // Update channel's last activity
         await updateDoc(doc(db, 'channels', activeChannel.id), {
            lastActivity: serverTimestamp(),
            updatedAt: serverTimestamp(),
         });

         // Log message activity for organization tracking
         await addDoc(collection(db, 'organizations', id, 'activityLogs'), {
            userId: currentUser.uid,
            action: 'message_sent',
            details: {
               channelId: activeChannel.id,
               channelName: activeChannel.name,
               hasAttachments: attachments.length > 0,
               attachmentCount: attachments.length,
            },
            timestamp: serverTimestamp(),
         });

         // Update recently used assets
         if (selectedAssets.length > 0) {
            // Add the assets to the beginning of the recentlyUsedAssets array
            const updatedRecentAssets = [...selectedAssets];

            // Filter out any duplicates from the existing recentlyUsedAssets
            recentlyUsedAssets.forEach((asset) => {
               if (!updatedRecentAssets.some((a) => a.id === asset.id)) {
                  updatedRecentAssets.push(asset);
               }
            });

            // Keep only the most recent 10 assets
            setRecentlyUsedAssets(updatedRecentAssets.slice(0, 10));

            // Optionally, store this in localStorage to persist across sessions
            try {
               localStorage.setItem(
                  `org_${id}_recent_assets`,
                  JSON.stringify(updatedRecentAssets.slice(0, 10))
               );
            } catch (e) {
               console.warn('Failed to save recent assets to localStorage:', e);
            }
         }

         setMessage('');
         setSelectedAssets([]);
      } catch (err) {
         console.error('Error sending message:', err);
         setError('Failed to send message. Please try again.');
      }
   };

   const handleAssetSelected = (assets: Asset[]) => {
      setSelectedAssets(assets);
   };

   const handleOpenAssetSelector = () => {
      setAssetSelectorOpen(true);
   };

   const handleRemoveAsset = (assetId: string) => {
      setSelectedAssets(selectedAssets.filter((asset) => asset.id !== assetId));
   };

   const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
         e.preventDefault();
         handleSendMessage();
      }
   };

   const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
   };

   const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
   };

   const handleMenuClose = () => {
      setAnchorEl(null);
   };

   const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
      setCurrentTab(newValue);
   };

   const formatTime = (date: Date): string => {
      return date.toLocaleTimeString('en-US', {
         hour: '2-digit',
         minute: '2-digit',
      });
   };

   const formatChannelLastActivity = (date?: Date): string => {
      if (!date) return '';

      const now = new Date();
      const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

      if (diffMinutes < 1) {
         return 'Just now';
      } else if (diffMinutes < 60) {
         return `${diffMinutes}m ago`;
      } else if (diffMinutes < 1440) {
         return `${Math.floor(diffMinutes / 60)}h ago`;
      } else {
         return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
         });
      }
   };

   const handleCreateChannel = () => {
      setNewChannelDialogOpen(true);
   };

   const handleChannelCreated = () => {
      // Channels will refresh automatically via the onSnapshot listener
   };

   const handleTabChange = (
      newTab: 'all' | 'events' | 'general' | 'direct' | 'subscriptions' | 'trips'
   ) => {
      setActiveTab(newTab);
   };

   const handleMemberClick = async (member: OrganizationMember) => {
      // Check if a direct message channel already exists with this member
      const existingChannel = channels.find(
         (channel) =>
            channel.type === 'direct' &&
            (channel.memberDetail?.userId === member.userId || channel.name === member.displayName)
      );

      if (existingChannel) {
         // If channel exists, select it
         handleChannelSelect(existingChannel);
      } else {
         // Otherwise, create a new direct message channel
         try {
            if (!id || !currentUser?.uid || !member.userId) {
               console.error('Missing required IDs for creating channel');
               setError('Could not create channel: missing required information');
               return;
            }

            // Create channel in Firestore
            const newChannelRef = await addDoc(collection(db, 'channels'), {
               name: member.displayName || member.email,
               displayName: member.displayName || member.email,
               type: 'direct',
               members: [currentUser.uid, member.userId],
               memberCount: 2,
               unreadCount: 0,
               createdAt: serverTimestamp(),
               lastActivity: serverTimestamp(),
               organizationId: id,
               memberDetail: {
                  userId: member.userId,
                  displayName: member.displayName,
                  email: member.email,
                  photoURL: member.photoURL,
                  avatarUrl: member.avatarUrl,
                  role: member.role,
               },
            });

            // Create welcome message
            await addDoc(collection(db, 'channels', newChannelRef.id, 'messages'), {
               content: `This is the beginning of your direct message conversation with ${member.displayName || member.email}.`,
               sender: {
                  id: 'system',
                  name: 'System',
               },
               timestamp: serverTimestamp(),
               isRead: false,
            });

            // Fetch the newly created channel to select it
            const newChannel: Channel = {
               id: newChannelRef.id,
               name: member.displayName || member.email,
               displayName: member.displayName || member.email,
               type: 'direct',
               members: [currentUser.uid, member.userId] as string[],
               memberCount: 2,
               unreadCount: 0,
               lastActivity: new Date(),
               memberDetail: member,
            };

            // Select the new channel
            handleChannelSelect(newChannel);
         } catch (error) {
            console.error('Error creating direct message channel:', error);
            setError('Failed to create direct message channel');
         }
      }
   };

   useEffect(() => {
      // Update filtered channels when channels or active tab changes
      if (channels.length === 0) {
         setFilteredChannels([]);
         return;
      }

      switch (activeTab) {
         case 'events':
            // Filter for event-related channels - look for type 'event' or event in name/description
            setFilteredChannels(
               channels.filter(
                  (channel) =>
                     channel.type === 'event' ||
                     channel.name.toLowerCase().includes('event') ||
                     channel.description?.toLowerCase().includes('event')
               )
            );
            break;
         case 'general':
            // Filter for general channels (public channels that aren't event-specific)
            setFilteredChannels(
               channels.filter(
                  (channel) =>
                     channel.type === 'public' && !channel.name.toLowerCase().includes('event')
               )
            );
            break;
         case 'direct':
            // Direct messages between members
            setFilteredChannels(channels.filter((channel) => channel.type === 'direct'));
            break;
         case 'subscriptions':
            setFilteredChannels(subscriptionChannels);
            break;
         case 'trips':
            setFilteredChannels(tripChannels);
            break;
         case 'all':
         default:
            // Show all channels by default (including direct ones)
            setFilteredChannels(channels);
            break;
      }
   }, [channels, activeTab, subscriptionChannels, tripChannels]);

   // Function to fetch and update subscription channels
   const fetchSubscriptions = async () => {
      if (!id || !currentUser) return;

      try {
         // Fetch active subscriptions for the organization
         const subscriptionsQuery = query(
            collection(db, 'subscriptions'),
            where('organizationId', '==', id),
            where('active', '==', true)
         );

         const subscriptionSnapshot = await getDocs(subscriptionsQuery);

         const activeSubscriptions: Subscription[] = subscriptionSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
         })) as Subscription[];

         if (activeSubscriptions.length === 0) {
            // Create a default subscription channel if none exist

            const defaultSubscriptionData = {
               type: 'subscription',
               entityId: 'default-subscription',
               entityName: 'Premium Membership',
               description: 'Channel for premium members of the organization',
               createdAt: serverTimestamp(),
               createdBy: currentUser.uid,
               members: [], // Will add members below
               memberCount: 0,
               organizationId: id,
               lastActivity: serverTimestamp(),
            };

            const channelRef = await addDoc(collection(db, 'channels'), defaultSubscriptionData);
            const channelId = channelRef.id;

            // Create welcome message
            await addDoc(collection(db, 'channels', channelRef.id, 'messages'), {
               content: `Welcome to the Premium Membership channel! This is where subscribers can discuss benefits and get exclusive updates.`,
               senderId: currentUser.uid,
               senderName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Admin',
               senderAvatar: currentUser.photoURL || '',
               timestamp: serverTimestamp(),
               isRead: false,
            });

            const subscriptionChannelsList: Channel[] = [
               {
                  id: channelId,
                  name: defaultSubscriptionData.entityName,
                  displayName: defaultSubscriptionData.entityName,
                  description: defaultSubscriptionData.description,
                  type: 'subscription',
                  members: [],
                  memberCount: 0,
                  unreadCount: 0,
                  lastActivity: new Date(),
               },
            ];

            // Now add all members of the organization
            if (orgMembers.length > 0) {
               const memberIds = orgMembers.map((member) => member.userId);

               await updateDoc(doc(db, 'channels', channelId), {
                  members: memberIds,
                  memberCount: memberIds.length,
                  updatedAt: serverTimestamp(),
               });

               subscriptionChannelsList[0].members = memberIds;
               subscriptionChannelsList[0].memberCount = memberIds.length;
            }

            setSubscriptionChannels(subscriptionChannelsList);

            return;
         }

         // Create channels for these subscriptions if they don't exist
         const subscriptionChannelsList: Channel[] = [];

         for (const subscription of activeSubscriptions) {
            // Check if channel already exists for this subscription
            const channelQuery = query(
               collection(db, 'channels'),
               where('type', '==', 'subscription'),
               where('entityId', '==', subscription.id)
            );

            const channelSnapshot = await getDocs(channelQuery);

            let channelId: string;

            if (!channelSnapshot.empty) {
               // Channel exists, use it
               channelId = channelSnapshot.docs[0].id;
               const channelData = channelSnapshot.docs[0].data();

               subscriptionChannelsList.push({
                  id: channelId,
                  name: channelData.entityName || `Subscription ${subscription.id.substr(0, 6)}`,
                  displayName:
                     channelData.entityName || `Subscription ${subscription.id.substr(0, 6)}`,
                  description:
                     channelData.description ||
                     `Channel for ${subscription.name || 'subscription'} subscribers`,
                  type: 'subscription',
                  members: channelData.members || [],
                  memberCount: channelData.memberCount || 0,
                  unreadCount: channelData.unreadCount || 0,
                  lastActivity: channelData.lastActivity ? channelData.lastActivity.toDate() : null,
               });
            } else {
               // Create a new channel for this subscription
               const channelData = {
                  type: 'subscription',
                  entityId: subscription.id,
                  entityName: subscription.name || `Subscription ${subscription.id.substr(0, 6)}`,
                  description: `Channel for ${subscription.name || 'subscription'} subscribers`,
                  createdAt: serverTimestamp(),
                  createdBy: currentUser.uid,
                  members: [], // Will add members below
                  memberCount: 0,
                  organizationId: id,
                  lastActivity: serverTimestamp(),
               };

               const channelRef = await addDoc(collection(db, 'channels'), channelData);
               channelId = channelRef.id;

               // Create welcome message
               await addDoc(collection(db, 'channels', channelRef.id, 'messages'), {
                  content: `Welcome to the ${subscription.name || 'subscription'} channel! This is where subscribers can discuss benefits and get exclusive updates.`,
                  senderId: currentUser.uid,
                  senderName:
                     currentUser.displayName || currentUser.email?.split('@')[0] || 'Admin',
                  senderAvatar: currentUser.photoURL || '',
                  timestamp: serverTimestamp(),
                  isRead: false,
               });

               subscriptionChannelsList.push({
                  id: channelId,
                  name: channelData.entityName,
                  displayName: channelData.entityName,
                  description: channelData.description,
                  type: 'subscription',
                  members: [],
                  memberCount: 0,
                  unreadCount: 0,
                  lastActivity: new Date(),
               });
            }

            // Now add all members of the organization to this channel if not already members
            if (orgMembers.length > 0) {
               const channelRef = doc(db, 'channels', channelId);
               const channelDoc = await getDoc(channelRef);

               if (channelDoc.exists()) {
                  const channelData = channelDoc.data();
                  const existingMembers = channelData.members || [];

                  // Get all user IDs from org members
                  const memberIds = orgMembers.map((member) => member.userId);

                  // Find members not already in the channel
                  const newMembers = memberIds.filter((id) => !existingMembers.includes(id));

                  if (newMembers.length > 0) {
                     // Update the channel members
                     await updateDoc(channelRef, {
                        members: [...existingMembers, ...newMembers],
                        memberCount: existingMembers.length + newMembers.length,
                        updatedAt: serverTimestamp(),
                     });

                     // Update our local channel object
                     const channelIndex = subscriptionChannelsList.findIndex(
                        (c) => c.id === channelId
                     );
                     if (channelIndex !== -1) {
                        subscriptionChannelsList[channelIndex].members = [
                           ...existingMembers,
                           ...newMembers,
                        ];
                        subscriptionChannelsList[channelIndex].memberCount =
                           existingMembers.length + newMembers.length;
                     }
                  }
               }
            }
         }

         // Update state with subscription channels
         setSubscriptionChannels(subscriptionChannelsList);

         // If activeTab is 'subscriptions', update filteredChannels
         if (activeTab === 'subscriptions') {
            setFilteredChannels(subscriptionChannelsList);
         }
      } catch (error) {
         console.error('Error fetching subscriptions:', error);
      }
   };

   useEffect(() => {
      fetchSubscriptions();
   }, [id, currentUser, orgMembers]);

   useEffect(() => {
      if (channels.length === 0) return;

      // Create a map of existing channel IDs to avoid duplicates
      const existingChannelIds = new Set(channels.map((channel) => channel.id));

      // Filter subscription channels that aren't already in the main channels array
      const newSubscriptionChannels = subscriptionChannels.filter(
         (channel) => !existingChannelIds.has(channel.id)
      );

      // Filter trip channels that aren't already in the main channels array
      const newTripChannels = tripChannels.filter((channel) => !existingChannelIds.has(channel.id));

      // If we have new channels to add
      if (newSubscriptionChannels.length > 0 || newTripChannels.length > 0) {
         // Create a new array with all channels
         const allChannels = [...channels, ...newSubscriptionChannels, ...newTripChannels];

         // Update the channels state
         setChannels(allChannels);

         // If we're in the "all" tab, update filtered channels too
         if (activeTab === 'all') {
            setFilteredChannels(allChannels);
         }
      }
   }, [channels, subscriptionChannels, tripChannels]);

   // Function to fetch and create trip channels
   const fetchTrips = async () => {
      if (!id || !currentUser) return;

      try {
         // Fetch trips for the organization
         const tripsQuery = query(
            collection(db, 'trips'),
            where('organizationId', '==', id),
            where('status', 'in', ['active', 'upcoming'])
         );

         const tripSnapshot = await getDocs(tripsQuery);

         interface Trip {
            id: string;
            title?: string;
            destination?: string;
            organizationId: string;
            status: string;
            [key: string]: any; // Allow for other properties
         }

         const activeTrips = tripSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
         })) as Trip[];

         if (activeTrips.length === 0) {
            // First try to get any trips regardless of status to show trip names
            const allTripsQuery = query(collection(db, 'trips'), where('organizationId', '==', id));

            const allTripsSnapshot = await getDocs(allTripsQuery);
            const allTrips = allTripsSnapshot.docs.map((doc) => ({
               id: doc.id,
               ...doc.data(),
            })) as Trip[];

            // Create channels for up to 3 trips, even if inactive
            const tripChannelsList: Channel[] = [];

            if (allTrips.length > 0) {
               // Use actual trips but limited to 3
               const tripsToUse = allTrips.slice(0, 3);

               for (const trip of tripsToUse) {
                  const tripName =
                     trip.title || trip.destination || `Trip to ${trip.destination || 'Unknown'}`;

                  const tripChannelData = {
                     type: 'trip',
                     entityId: trip.id,
                     entityName: tripName,
                     description: `Channel for ${tripName} discussions`,
                     createdAt: serverTimestamp(),
                     createdBy: currentUser.uid,
                     members: [], // Will add members below
                     memberCount: 0,
                     organizationId: id,
                     lastActivity: serverTimestamp(),
                  };

                  const channelRef = await addDoc(collection(db, 'channels'), tripChannelData);
                  const channelId = channelRef.id;

                  // Create welcome message
                  await addDoc(collection(db, 'channels', channelRef.id, 'messages'), {
                     content: `Welcome to the ${tripName} channel! This is where travelers can discuss plans, share updates, and coordinate during the trip.`,
                     senderId: currentUser.uid,
                     senderName:
                        currentUser.displayName || currentUser.email?.split('@')[0] || 'Admin',
                     senderAvatar: currentUser.photoURL || '',
                     timestamp: serverTimestamp(),
                     isRead: false,
                  });

                  const channelItem: Channel = {
                     id: channelId,
                     name: tripChannelData.entityName,
                     displayName: tripChannelData.entityName,
                     description: tripChannelData.description,
                     type: 'trip',
                     members: [],
                     memberCount: 0,
                     unreadCount: 0,
                     lastActivity: new Date(),
                  };

                  // Now add all members of the organization
                  if (orgMembers.length > 0) {
                     const memberIds = orgMembers.map((member) => member.userId);

                     await updateDoc(doc(db, 'channels', channelId), {
                        members: memberIds,
                        memberCount: memberIds.length,
                        updatedAt: serverTimestamp(),
                     });

                     channelItem.members = memberIds;
                     channelItem.memberCount = memberIds.length;
                  }

                  tripChannelsList.push(channelItem);
               }

               setTripChannels(tripChannelsList);

               return;
            } else {
               // Still no trips found, create a default channel

               const defaultTripData = {
                  type: 'trip',
                  entityId: 'default-trip',
                  entityName: 'Group Travel',
                  description: 'Channel for organization travel discussions',
                  createdAt: serverTimestamp(),
                  createdBy: currentUser.uid,
                  members: [], // Will add members below
                  memberCount: 0,
                  unreadCount: 0,
                  organizationId: id,
                  lastActivity: serverTimestamp(),
               };

               const channelRef = await addDoc(collection(db, 'channels'), defaultTripData);
               const channelId = channelRef.id;

               // Create welcome message
               await addDoc(collection(db, 'channels', channelRef.id, 'messages'), {
                  content: `Welcome to the Group Travel channel! This is where members can discuss travel plans and coordinate trips.`,
                  senderId: currentUser.uid,
                  senderName:
                     currentUser.displayName || currentUser.email?.split('@')[0] || 'Admin',
                  senderAvatar: currentUser.photoURL || '',
                  timestamp: serverTimestamp(),
                  isRead: false,
               });

               const tripChannelsList: Channel[] = [
                  {
                     id: channelId,
                     name: defaultTripData.entityName,
                     displayName: defaultTripData.entityName,
                     description: defaultTripData.description,
                     type: 'trip',
                     members: [],
                     memberCount: 0,
                     unreadCount: 0,
                     lastActivity: new Date(),
                  },
               ];

               // Now add all members of the organization
               if (orgMembers.length > 0) {
                  const memberIds = orgMembers.map((member) => member.userId);

                  await updateDoc(doc(db, 'channels', channelId), {
                     members: memberIds,
                     memberCount: memberIds.length,
                     updatedAt: serverTimestamp(),
                  });

                  tripChannelsList[0].members = memberIds;
                  tripChannelsList[0].memberCount = memberIds.length;
               }

               setTripChannels(tripChannelsList);

               return;
            }
         }

         // Create channels for these trips if they don't exist
         const tripChannelsList: Channel[] = [];

         for (const trip of activeTrips) {
            // Check if channel already exists for this trip
            const channelQuery = query(
               collection(db, 'channels'),
               where('type', '==', 'trip'),
               where('entityId', '==', trip.id)
            );

            const channelSnapshot = await getDocs(channelQuery);

            let channelId: string;

            if (!channelSnapshot.empty) {
               // Channel exists, use it
               channelId = channelSnapshot.docs[0].id;
               const channelData = channelSnapshot.docs[0].data();

               tripChannelsList.push({
                  id: channelId,
                  name: channelData.entityName || `Trip ${trip.id.substr(0, 6)}`,
                  displayName: channelData.entityName || `Trip ${trip.id.substr(0, 6)}`,
                  description:
                     channelData.description ||
                     `Channel for ${trip.title || trip.destination || 'trip'} travelers`,
                  type: 'trip',
                  members: channelData.members || [],
                  memberCount: channelData.memberCount || 0,
                  unreadCount: channelData.unreadCount || 0,
                  lastActivity: channelData.lastActivity ? channelData.lastActivity.toDate() : null,
               });
            } else {
               // Create a new channel for this trip
               const channelData = {
                  type: 'trip',
                  entityId: trip.id,
                  entityName: trip.title || trip.destination || `Trip ${trip.id.substr(0, 6)}`,
                  description: `Channel for ${trip.title || trip.destination || 'trip'} travelers`,
                  createdAt: serverTimestamp(),
                  createdBy: currentUser.uid,
                  members: [], // Will add members below
                  memberCount: 0,
                  unreadCount: 0,
                  organizationId: id,
                  lastActivity: serverTimestamp(),
               };

               const channelRef = await addDoc(collection(db, 'channels'), channelData);
               channelId = channelRef.id;

               // Create welcome message
               await addDoc(collection(db, 'channels', channelRef.id, 'messages'), {
                  content: `Welcome to the ${trip.title || trip.destination || 'trip'} channel! This is where travelers can discuss plans, share updates, and coordinate during the trip.`,
                  senderId: currentUser.uid,
                  senderName:
                     currentUser.displayName || currentUser.email?.split('@')[0] || 'Admin',
                  senderAvatar: currentUser.photoURL || '',
                  timestamp: serverTimestamp(),
                  isRead: false,
               });

               tripChannelsList.push({
                  id: channelId,
                  name: channelData.entityName,
                  displayName: channelData.entityName,
                  description: channelData.description,
                  type: 'trip',
                  members: [],
                  memberCount: 0,
                  unreadCount: 0,
                  lastActivity: new Date(),
               });
            }

            // Now add all members of the organization to this trip channel if not already members
            if (orgMembers.length > 0) {
               const channelRef = doc(db, 'channels', channelId);
               const channelDoc = await getDoc(channelRef);

               if (channelDoc.exists()) {
                  const channelData = channelDoc.data();
                  const existingMembers = channelData.members || [];

                  // Get all user IDs from org members
                  const memberIds = orgMembers.map((member) => member.userId);

                  // Find members not already in the channel
                  const newMembers = memberIds.filter((id) => !existingMembers.includes(id));

                  if (newMembers.length > 0) {
                     // Update the channel members
                     await updateDoc(channelRef, {
                        members: [...existingMembers, ...newMembers],
                        memberCount: existingMembers.length + newMembers.length,
                        updatedAt: serverTimestamp(),
                     });

                     // Update our local channel object
                     const channelIndex = tripChannelsList.findIndex((c) => c.id === channelId);
                     if (channelIndex !== -1) {
                        tripChannelsList[channelIndex].members = [
                           ...existingMembers,
                           ...newMembers,
                        ];
                        tripChannelsList[channelIndex].memberCount =
                           existingMembers.length + newMembers.length;
                     }
                  }
               }
            }
         }

         // Update state with trip channels
         setTripChannels(tripChannelsList);

         // If activeTab is 'trips', update filteredChannels
         if (activeTab === 'trips') {
            setFilteredChannels(tripChannelsList);
         }
      } catch (error) {
         console.error('Error fetching trips:', error);
      }
   };

   useEffect(() => {
      fetchTrips();
   }, [id, currentUser, orgMembers]);

   useEffect(() => {
      const loadRecentAssets = () => {
         try {
            const savedAssets = localStorage.getItem(`org_${id}_recent_assets`);
            if (savedAssets) {
               const parsedAssets = JSON.parse(savedAssets);
               if (Array.isArray(parsedAssets) && parsedAssets.length > 0) {
                  setRecentlyUsedAssets(parsedAssets);
               }
            }
         } catch (e) {
            console.warn('Failed to load recent assets from localStorage:', e);
         }
      };

      if (id) {
         loadRecentAssets();
      }
   }, [id]);

   useEffect(() => {
      // Scroll to bottom when messages change
      if (messagesEndRef.current) {
         messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
   }, [messages]);

   useEffect(() => {
      // Add keyboard shortcuts for assets
      const handleKeyboardShortcuts = (e: KeyboardEvent) => {
         // Only trigger when the chat view is active (indicated by activeChannel being set)
         if (!activeChannel) return;

         // Ctrl+Shift+A to open asset gallery
         if (e.ctrlKey && e.shiftKey && e.key === 'A') {
            e.preventDefault();
            handleOpenAssetSelector();
         }
      };

      window.addEventListener('keydown', handleKeyboardShortcuts);

      return () => {
         window.removeEventListener('keydown', handleKeyboardShortcuts);
      };
   }, [activeChannel]);

   if (loading) {
      return (
         <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}
         >
            <CircularProgress />
         </Box>
      );
   }

   if (error) {
      return (
         <Container maxWidth='lg' sx={{ mt: 4 }}>
            <Alert severity='error'>{error}</Alert>
         </Container>
      );
   }

   return (
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
         {/* Channels Sidebar */}
         <Drawer
            variant='persistent'
            anchor='left'
            open={sidebarOpen}
            sx={{
               width: 280,
               flexShrink: 0,
               '& .MuiDrawer-paper': {
                  width: 280,
                  boxSizing: 'border-box',
                  position: 'relative',
                  height: '100%',
                  border: 'none',
                  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
               },
            }}
         >
            <Box
               sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
               <Typography variant='h6'>
                  {activeOrganization?.name || 'Organization'} Chat
               </Typography>
               <IconButton onClick={() => setSidebarOpen(false)}>
                  <MoreVertIcon />
               </IconButton>
            </Box>

            <Box
               sx={{
                  px: 2,
                  pb: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
               }}
            >
               <ButtonGroup variant='outlined' aria-label='channel filter button group'>
                  <Button
                     variant={activeTab === 'all' ? 'contained' : 'outlined'}
                     onClick={() => handleTabChange('all')}
                  >
                     All
                  </Button>
                  <Button
                     variant={activeTab === 'events' ? 'contained' : 'outlined'}
                     onClick={() => handleTabChange('events')}
                  >
                     Events
                  </Button>
                  <Button
                     variant={activeTab === 'general' ? 'contained' : 'outlined'}
                     onClick={() => handleTabChange('general')}
                  >
                     General
                  </Button>
                  <Button
                     variant={activeTab === 'direct' ? 'contained' : 'outlined'}
                     onClick={() => handleTabChange('direct')}
                  >
                     Direct
                  </Button>
                  <Button
                     variant={activeTab === 'subscriptions' ? 'contained' : 'outlined'}
                     onClick={() => handleTabChange('subscriptions')}
                  >
                     Subscriptions
                  </Button>
                  <Button
                     variant={activeTab === 'trips' ? 'contained' : 'outlined'}
                     onClick={() => handleTabChange('trips')}
                  >
                     Trips
                  </Button>
               </ButtonGroup>

               <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleCreateChannel}
                  size='small'
                  sx={{ mt: 1 }}
               >
                  New Channel
               </Button>
            </Box>

            <Tabs
               value={currentTab}
               onChange={handleChangeTab}
               sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
               <Tab label='Channels' id='tab-0' />
               <Tab label='Members' id='tab-1' />
            </Tabs>

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
               {/* Tab panel for channels */}
               <Box
                  role='tabpanel'
                  hidden={currentTab !== 0}
                  sx={{ height: '100%', overflow: 'auto' }}
               >
                  {currentTab === 0 && (
                     <List sx={{ pt: 0 }}>
                        {filteredChannels.length > 0 ? (
                           filteredChannels.map((channel) => (
                              <ListItem
                                 key={channel.id}
                                 button
                                 selected={activeChannel?.id === channel.id}
                                 onClick={() => handleChannelSelect(channel)}
                                 sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    py: 1.5,
                                 }}
                              >
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       width: '100%',
                                    }}
                                 >
                                    <Box sx={{ mr: 1.5 }}>
                                       <GroupsIcon fontSize='small' color='action' />
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             justifyContent: 'space-between',
                                             alignItems: 'center',
                                          }}
                                       >
                                          <Typography
                                             variant='body2'
                                             sx={{
                                                fontWeight:
                                                   channel.unreadCount > 0 ? 'bold' : 'regular',
                                             }}
                                          >
                                             {channel.displayName || channel.name}
                                          </Typography>
                                          {channel.lastActivity && (
                                             <Typography variant='caption' color='text.secondary'>
                                                {formatChannelLastActivity(channel.lastActivity)}
                                             </Typography>
                                          )}
                                       </Box>
                                       <Typography variant='caption' color='text.secondary' noWrap>
                                          {channel.description ||
                                             (typeof channel.members === 'number'
                                                ? `${channel.members} members`
                                                : `${channel.members.length} members`)}
                                       </Typography>
                                       {channel.unreadCount > 0 && (
                                          <Chip
                                             label={channel.unreadCount}
                                             color='primary'
                                             size='small'
                                             sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                '& .MuiChip-label': {
                                                   px: 1,
                                                },
                                             }}
                                          />
                                       )}
                                    </Box>
                                 </Box>
                              </ListItem>
                           ))
                        ) : (
                           <Box sx={{ p: 3, textAlign: 'center' }}>
                              <Typography color='text.secondary'>
                                 No channels match the selected filter.
                              </Typography>
                              <Button
                                 variant='outlined'
                                 size='small'
                                 onClick={handleCreateChannel}
                                 startIcon={<AddIcon />}
                                 sx={{ mt: 2 }}
                              >
                                 Create New Channel
                              </Button>
                           </Box>
                        )}
                     </List>
                  )}
               </Box>

               {/* Tab panel for members */}
               <Box
                  role='tabpanel'
                  hidden={currentTab !== 1}
                  sx={{ height: '100%', overflow: 'auto' }}
               >
                  {currentTab === 1 && (
                     <List sx={{ pt: 0 }}>
                        {loadingMembers ? (
                           <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                              <CircularProgress size={24} />
                           </Box>
                        ) : orgMembers.length > 0 ? (
                           orgMembers.map((member) => (
                              <ListItem
                                 key={member.id}
                                 button
                                 onClick={() => handleMemberClick(member)}
                                 sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    py: 1.5,
                                 }}
                              >
                                 <ListItemAvatar>
                                    <Avatar
                                       src={member.avatarUrl || member.photoURL}
                                       alt={member.displayName || member.email}
                                    >
                                       {(member.displayName ||
                                          member.email ||
                                          '?')[0].toUpperCase()}
                                    </Avatar>
                                 </ListItemAvatar>
                                 <ListItemText
                                    primary={
                                       <Typography variant='body2'>
                                          {member.displayName || member.email}
                                       </Typography>
                                    }
                                    secondary={
                                       <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                          <Chip
                                             label={member.role}
                                             size='small'
                                             color={member.role === 'admin' ? 'primary' : 'default'}
                                             variant='outlined'
                                             sx={{ height: 20, fontSize: '0.7rem' }}
                                          />
                                       </Box>
                                    }
                                 />
                              </ListItem>
                           ))
                        ) : (
                           <Box sx={{ p: 3, textAlign: 'center' }}>
                              <Typography color='text.secondary'>
                                 No members found for this organization.
                              </Typography>
                           </Box>
                        )}
                     </List>
                  )}
               </Box>
            </Box>
         </Drawer>

         {/* Main Chat Area */}
         <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Chat Header */}
            <AppBar
               position='static'
               color='default'
               elevation={0}
               sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}
            >
               <Toolbar variant='dense'>
                  <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mr: 1 }}>
                     {activeChannel?.type === 'direct'
                        ? activeChannel.name
                        : `#${activeChannel?.name}`}
                  </Typography>
                  {activeChannel?.description && (
                     <>
                        <Divider orientation='vertical' flexItem sx={{ mx: 1 }} />
                        <Typography variant='body2' color='text.secondary' noWrap>
                           {activeChannel.description}
                        </Typography>
                     </>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton size='small'>
                     <MembersIcon fontSize='small' />
                  </IconButton>
                  <IconButton size='small' onClick={handleMenuOpen}>
                     <MoreVertIcon fontSize='small' />
                  </IconButton>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                     <MenuItem onClick={handleMenuClose}>Channel Settings</MenuItem>
                     <MenuItem onClick={handleMenuClose}>Add Members</MenuItem>
                     <MenuItem onClick={handleMenuClose}>Mute Channel</MenuItem>
                     <Divider />
                     <MenuItem onClick={handleMenuClose}>Leave Channel</MenuItem>
                  </Menu>
               </Toolbar>
            </AppBar>

            {/* Messages */}
            <Box
               sx={{ flexGrow: 1, overflow: 'auto', p: 2, backgroundColor: 'background.default' }}
            >
               <List>
                  {messages.map((msg, index) => {
                     const isCurrentUser = currentUser && msg.sender.id === currentUser.uid;
                     const showAvatar =
                        index === 0 || messages[index - 1].sender.id !== msg.sender.id;

                     return (
                        <ListItem
                           key={msg.id}
                           alignItems='flex-start'
                           sx={{
                              flexDirection: 'column',
                              alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
                              py: 0.5,
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                                 alignItems: 'flex-start',
                                 maxWidth: '80%',
                              }}
                           >
                              {showAvatar && !isCurrentUser && (
                                 <ListItemAvatar sx={{ minWidth: 40 }}>
                                    <Avatar
                                       src={msg.sender.avatarUrl}
                                       sx={{ width: 32, height: 32 }}
                                    >
                                       {msg.sender.name.charAt(0)}
                                    </Avatar>
                                 </ListItemAvatar>
                              )}
                              <Box>
                                 {showAvatar && (
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          mb: 0.5,
                                          flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                                       }}
                                    >
                                       <Typography
                                          variant='body2'
                                          sx={{
                                             fontWeight: 'bold',
                                             mr: isCurrentUser ? 0 : 1,
                                             ml: isCurrentUser ? 1 : 0,
                                          }}
                                       >
                                          {msg.sender.name}
                                       </Typography>
                                       <Typography variant='caption' color='text.secondary'>
                                          {formatTime(msg.timestamp)}
                                       </Typography>
                                    </Box>
                                 )}
                                 <Paper
                                    sx={{
                                       p: 1.5,
                                       backgroundColor: isCurrentUser
                                          ? 'primary.light'
                                          : 'background.paper',
                                       borderRadius: 2,
                                       color: isCurrentUser
                                          ? 'primary.contrastText'
                                          : 'text.primary',
                                       maxWidth: '100%',
                                    }}
                                 >
                                    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                                       {msg.content}
                                    </Typography>
                                 </Paper>
                                 {msg.attachments && msg.attachments.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                       <Grid container spacing={1}>
                                          {msg.attachments.map((attachment) => (
                                             <Grid item key={attachment.id} xs={6} sm={4} md={3}>
                                                <Card
                                                   elevation={0}
                                                   sx={{
                                                      minWidth: 80,
                                                      width: 80,
                                                      height: 80,
                                                      position: 'relative',
                                                      borderRadius: 1,
                                                      mr: 1,
                                                      transition: 'all 0.2s ease-in-out',
                                                      '&:hover': {
                                                         transform: 'scale(1.03)',
                                                         boxShadow: 2,
                                                      },
                                                   }}
                                                   onClick={() =>
                                                      window.open(
                                                         attachment.url || attachment.imageUrl,
                                                         '_blank'
                                                      )
                                                   }
                                                >
                                                   {attachment.type?.startsWith('image') ||
                                                   attachment.imageUrl ? (
                                                      <CardMedia
                                                         component='img'
                                                         height='60'
                                                         image={
                                                            attachment.thumbnailUrl ||
                                                            attachment.imageUrl ||
                                                            attachment.url
                                                         }
                                                         alt={attachment.name}
                                                         sx={{ objectFit: 'cover' }}
                                                         onError={(e: any) => {
                                                            console.error(
                                                               'Image failed to load:',
                                                               attachment
                                                            );
                                                            // Fallback for failed images
                                                            e.target.onerror = null;
                                                            e.target.src =
                                                               'https://via.placeholder.com/300x200?text=Image+Not+Available';
                                                         }}
                                                      />
                                                   ) : (
                                                      <Box
                                                         sx={{
                                                            height: 120,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: 'action.hover',
                                                         }}
                                                      >
                                                         <AttachFileIcon
                                                            sx={{
                                                               fontSize: 40,
                                                               color: 'text.secondary',
                                                            }}
                                                         />
                                                      </Box>
                                                   )}
                                                   <Box sx={{ p: 1 }}>
                                                      <Typography variant='caption' noWrap>
                                                         {attachment.name}
                                                      </Typography>
                                                   </Box>
                                                </Card>
                                             </Grid>
                                          ))}
                                       </Grid>
                                       <Typography
                                          variant='caption'
                                          color='text.secondary'
                                          sx={{ display: 'block', mt: 0.5 }}
                                       >
                                          {msg.attachments.length}{' '}
                                          {msg.attachments.length === 1 ? 'asset' : 'assets'} shared
                                          • Click to view full size
                                       </Typography>
                                    </Box>
                                 )}
                              </Box>
                           </Box>
                        </ListItem>
                     );
                  })}
                  <div ref={messagesEndRef} />
               </List>
            </Box>

            {/* Message Input */}
            <Paper
               elevation={0}
               sx={{
                  p: 2,
                  backgroundColor: 'background.paper',
                  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
               }}
            >
               {recentlyUsedAssets.length > 0 && !selectedAssets.length && (
                  <Box sx={{ mb: 2 }}>
                     <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mb: 1, display: 'block' }}
                     >
                        Recently shared assets:
                     </Typography>
                     <Box
                        sx={{
                           display: 'flex',
                           overflowX: 'auto',
                           pb: 1,
                           '&::-webkit-scrollbar': {
                              height: 6,
                           },
                           '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.2)',
                              borderRadius: 3,
                           },
                        }}
                     >
                        {recentlyUsedAssets.map((asset) => (
                           <Card
                              key={asset.id}
                              variant='outlined'
                              sx={{
                                 minWidth: 80,
                                 width: 80,
                                 height: 80,
                                 position: 'relative',
                                 borderRadius: 1,
                                 mr: 1,
                                 transition: 'all 0.2s ease-in-out',
                                 '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 2,
                                    cursor: 'pointer',
                                 },
                                 flexShrink: 0,
                              }}
                              onClick={() => setSelectedAssets([...selectedAssets, asset])}
                           >
                              <CardMedia
                                 component='img'
                                 height='60'
                                 image={asset.thumbnailUrl || asset.imageUrl}
                                 alt={asset.name}
                                 sx={{ objectFit: 'cover' }}
                                 onError={(e: any) => {
                                    // Fallback for failed images
                                    e.target.onerror = null;
                                    e.target.src = 'https://via.placeholder.com/150?text=Image';
                                 }}
                              />
                              <Box sx={{ p: 0.5 }}>
                                 <Typography
                                    variant='caption'
                                    noWrap
                                    sx={{
                                       fontSize: '0.65rem',
                                       textAlign: 'center',
                                       display: 'block',
                                    }}
                                 >
                                    {asset.name}
                                 </Typography>
                              </Box>
                           </Card>
                        ))}
                     </Box>
                  </Box>
               )}

               {selectedAssets.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                     <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mb: 1, display: 'block' }}
                     >
                        Selected assets to share:
                     </Typography>
                     <Grid container spacing={1}>
                        {selectedAssets.map((asset) => (
                           <Grid item key={asset.id} xs={4} sm={3} md={2}>
                              <Card
                                 variant='outlined'
                                 sx={{
                                    position: 'relative',
                                    borderRadius: 1,
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                       transform: 'translateY(-2px)',
                                       boxShadow: 2,
                                    },
                                 }}
                              >
                                 <CardMedia
                                    component='img'
                                    height='60'
                                    image={asset.thumbnailUrl || asset.imageUrl}
                                    alt={asset.name}
                                    sx={{ objectFit: 'cover' }}
                                    onError={(e: any) => {
                                       // Fallback for failed images
                                       e.target.onerror = null;
                                       e.target.src = 'https://via.placeholder.com/150?text=Image';
                                    }}
                                 />
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: -8,
                                       right: -8,
                                       bgcolor: 'background.paper',
                                       borderRadius: '50%',
                                       width: 24,
                                       height: 24,
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       boxShadow: 1,
                                       '&:hover': {
                                          bgcolor: 'error.light',
                                          color: 'error.contrastText',
                                       },
                                       cursor: 'pointer',
                                    }}
                                    onClick={() => handleRemoveAsset(asset.id)}
                                    title='Remove asset'
                                 >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                 </Box>
                                 <Typography
                                    variant='caption'
                                    sx={{
                                       p: 0.5,
                                       display: 'block',
                                       overflow: 'hidden',
                                       textOverflow: 'ellipsis',
                                       whiteSpace: 'nowrap',
                                    }}
                                 >
                                    {asset.name}
                                 </Typography>
                              </Card>
                           </Grid>
                        ))}
                     </Grid>
                  </Box>
               )}
               {showAssetTip && (
                  <Box sx={{ mb: 2 }}>
                     <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mb: 1, display: 'block' }}
                     >
                        Tip: You can now share assets from the gallery by clicking the{' '}
                        <PhotoIcon sx={{ fontSize: 16 }} /> button or using the keyboard shortcut
                        Ctrl+Shift+A.
                     </Typography>
                     <Button variant='outlined' size='small' onClick={handleDismissAssetTip}>
                        Dismiss
                     </Button>
                  </Box>
               )}
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                     fullWidth
                     placeholder='Type a message...'
                     variant='outlined'
                     size='small'
                     value={message}
                     onChange={(e) => setMessage(e.target.value)}
                     onKeyPress={handleKeyPress}
                     multiline
                     maxRows={4}
                     sx={{ mr: 1 }}
                  />
                  <Button
                     color='primary'
                     variant='outlined'
                     startIcon={<PhotoIcon />}
                     onClick={handleOpenAssetSelector}
                     title='Share assets from gallery (Ctrl+Shift+A)'
                     size='small'
                     sx={{
                        minWidth: 'auto',
                        mr: 1,
                        display: { xs: 'none', sm: 'flex' },
                     }}
                  >
                     Gallery
                  </Button>
                  <IconButton
                     color='primary'
                     onClick={handleOpenAssetSelector}
                     title='Share assets from gallery (Ctrl+Shift+A)'
                     sx={{
                        mr: 1,
                        display: { xs: 'flex', sm: 'none' },
                     }}
                  >
                     <PhotoIcon />
                  </IconButton>
                  <IconButton
                     color='primary'
                     onClick={handleSendMessage}
                     disabled={message.trim() === '' && selectedAssets.length === 0}
                     sx={{
                        backgroundColor:
                           message.trim() !== '' || selectedAssets.length > 0
                              ? 'primary.main'
                              : undefined,
                        color:
                           message.trim() !== '' || selectedAssets.length > 0
                              ? 'primary.contrastText'
                              : undefined,
                        '&:hover': {
                           backgroundColor:
                              message.trim() !== '' || selectedAssets.length > 0
                                 ? 'primary.dark'
                                 : undefined,
                        },
                     }}
                  >
                     <SendIcon />
                  </IconButton>
               </Box>
            </Paper>
         </Box>
         <NewChannelDialog
            open={newChannelDialogOpen}
            onClose={() => setNewChannelDialogOpen(false)}
            organizationId={id || ''}
            onChannelCreated={handleChannelCreated}
         />
         <AssetSelectorDialog
            open={assetSelectorOpen}
            onClose={() => setAssetSelectorOpen(false)}
            organizationId={id || ''}
            onAssetSelected={handleAssetSelected}
         />
      </Box>
   );
};

export default OrganizationChat;
